import {
  GROUP_TYPE,
  IBroadcastInfo,
  IGroupInfo,
  IUserInfo,
  USER_AVAILABLE_STATUS,
} from "@home-health-notify/domain";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import CampaignIcon from "@mui/icons-material/Campaign";
import WorkIcon from "@mui/icons-material/Work";
import { Typography } from "@mui/material";
import { dateAgo, formatDisplayPhoneNumber } from "hhn-commons";
import React from "react";
import DisplayAvatarImage from "../../common/DisplayAvatarImage";
import theme from "../../theme";
import {
  getAvatarSize,
  getAvatarSrcAndAlt,
  GROUP_AVATAR_SIZE,
} from "../../utils/AvatarUtils";
import {
  AboutText,
  HeaderDescriptionContainer,
  HeaderTextWrapper,
  NameText,
} from "../MessageHeader.style";
import {
  UserDetails,
  UserDetailsWrapper,
} from "./privateChatHeader/PrivateHeader.style";

interface CommonChatHeaderDescriptionProps {
  chatInfo: IBroadcastInfo | IGroupInfo | IUserInfo;
  avatarSize?: number;
  showUserDescription?: boolean;
  showUserAbout?: boolean;
  showUserStatus?: boolean;
  maxWidth?: number;
  fontSizeTitle?: number;
}

export const CommonChatHeaderDescription = ({
  chatInfo,
  avatarSize,
  showUserDescription = false,
  showUserAbout = true,
  showUserStatus = true,
  maxWidth,
  fontSizeTitle,
}: CommonChatHeaderDescriptionProps) => {
  const groupInfo = chatInfo as IGroupInfo;
  const userInfo = chatInfo as IUserInfo;
  const isBroadcast = chatInfo.groupType === GROUP_TYPE.BROADCAST;
  const isPrivate = chatInfo.groupType === GROUP_TYPE.PRIVATE;

  return (
    <HeaderDescriptionContainer
      data-testid={`${getChatNameDataTestId(
        chatInfo.groupType,
      )}-header-description`}
    >
      <DisplayAvatarImage
        src={getAvatarSrcAndAlt(isPrivate ? userInfo : groupInfo).src}
        alt={getAvatarSrcAndAlt(isPrivate ? userInfo : groupInfo).alt}
        avatarSize={
          avatarSize ||
          getAvatarSize(groupInfo.groupType, GROUP_AVATAR_SIZE.SMALL)
        }
        staticIcon={isBroadcast ? CampaignIcon : undefined}
        showOnlineStatusBadge={
          isPrivate && !showUserDescription && showUserStatus
        }
        isOnline={userInfo.availableStatus === USER_AVAILABLE_STATUS.AVAILABLE}
      />
      <HeaderTextWrapper $maxWidth={maxWidth}>
        <NameText
          fontSize={16}
          data-testid={`${getChatNameDataTestId(chatInfo.groupType)}-${
            chatInfo.groupType !== GROUP_TYPE.PRIVATE ? "group" : "chat"
          }-name`}
        >
          {isPrivate ? (chatInfo as IUserInfo).title : chatInfo.name}
        </NameText>
        {showUserAbout && chatInfo.groupType !== GROUP_TYPE.PRIVATE ? (
          <AboutText
            color="secondary"
            data-testid={`${getChatNameDataTestId(
              chatInfo.groupType,
            )}-group-about`}
          >
            {chatInfo.about}
          </AboutText>
        ) : showUserDescription ? (
          <UserDetailsWrapper>
            <UserDetails>
              <WorkIcon
                sx={{
                  marginRight: "7px",
                }}
                fontSize="small"
                color="secondary"
              />
              <Typography data-testid="user-department" color="secondary">
                {userInfo.department?.name ?? "N/A"}
              </Typography>
            </UserDetails>
            <UserDetails>
              <AssignmentIndIcon
                sx={{
                  marginRight: "7px",
                }}
                fontSize="small"
                color="secondary"
              />
              <Typography color="secondary" data-testid="user-designation">
                {userInfo.designation?.name ?? "N/A"}
              </Typography>
            </UserDetails>
            <Typography>
              {!userInfo.countryCode || !userInfo.phoneNo ? null : (
                <a
                  href={"tel:" + userInfo.countryCode + userInfo.phoneNo}
                  style={{
                    color: `${theme.palette.secondary.main}`,
                    textDecoration: "none",
                  }}
                >
                  <span data-testid="phone-no">
                    {formatDisplayPhoneNumber(
                      userInfo.countryCode,
                      userInfo.phoneNo,
                    )}
                  </span>
                </a>
              )}
            </Typography>
            <Typography>
              {!userInfo.email ? null : (
                <a
                  href={"mailto:" + userInfo.email}
                  style={{
                    color: `${theme.palette.secondary.main}`,
                    textDecoration: "none",
                  }}
                >
                  <span data-testid="user-email">{userInfo.email}</span>
                </a>
              )}
            </Typography>
          </UserDetailsWrapper>
        ) : (
          <>
            {showUserStatus && (
              <Typography color="secondary">
                {userInfo.lastActive
                  ? dateAgo(userInfo.lastActive, userInfo.isOnline ?? false)
                  : "Offline"}
              </Typography>
            )}
          </>
        )}
      </HeaderTextWrapper>
    </HeaderDescriptionContainer>
  );
};

function getChatNameDataTestId(groupType: GROUP_TYPE): string {
  switch (groupType) {
    case GROUP_TYPE.NON_PATIENT:
      return "internal";
    case GROUP_TYPE.PRIVATE:
      return "private";
    case GROUP_TYPE.BROADCAST:
      return "broadcast";
    default:
      return "unknown";
  }
}
